import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../../../api/axiosConfig";
import "../../../css/HelpdeskQueryList.css";
import QueryModal from "./QueryModal";
import ClosedQueryModal from "./ClosedQueryModal";

export type TicketStatus =
  | "Received"
  | "In-progress"
  | "Awaiting Closure"
  | "Closed"
  | "Open";

export interface ITicket {
  _id: string;
  issueType: string;
  raisedBy: string;
  status: TicketStatus;
  createdAt: string;
  updatedAt: string;
  ageing: number;
  childName?: string;
  parentName?: string;
  centerName?: string;
  center?: string;
  venueName?: string;
  amountPaid?: number | string;
  amount?: number | string;
  finalAmount?: number | string;
  paymentDate?: string;
  appointmentDate?: string;
  screeningDate?: string;
  message?: string;
  statusAgeing?: number;
}

const HelpdeskQueryList: React.FC = () => {
  const [allTickets, setAllTickets] = useState<ITicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<ITicket | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | TicketStatus>("all");
  const [issueTypeFilter, setIssueTypeFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<"createdAt" | "issueType" | "status" | "updatedAt">("updatedAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const itemsPerPage = 10;

  const normalizeTicketStatus = (status?: string): TicketStatus => {
    const raw = (status || "").trim();
    if (!raw) return "Received";

    const lower = raw.toLowerCase();
    if (lower === "open") return "Received";

    return raw as TicketStatus;
  };

  const fetchTickets = useCallback(async () => {
    try {
      const res = await apiClient.helpdeskApi.get("/api/admin/tickets", {
        params: { page: currentPage, limit: itemsPerPage },
      });

      const normalizedTickets = (res.data.data || []).map((ticket: any) => ({
        ...ticket,
        status: normalizeTicketStatus(ticket.status),
      }));

      setAllTickets(normalizedTickets);
      setTotalTickets(res.data.total || 0);
    } catch (err) {
      console.error("Error fetching tickets:", err);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // Reset page to 1 whenever filters or sort parameters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, issueTypeFilter, sortField, sortOrder]);

  const issueTypeOptions = Array.from(
    new Set(allTickets.map(ticket => ticket.issueType).filter(Boolean))
  );

  const filteredTickets = [...allTickets]
    .filter(ticket => {
      const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
      const matchesIssueType = issueTypeFilter === "all" || ticket.issueType === issueTypeFilter;
      return matchesStatus && matchesIssueType;
    })
    .sort((a, b) => {
      const factor = sortOrder === "asc" ? 1 : -1;

      if (sortField === "createdAt") {
        return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * factor;
      }

      if (sortField === "issueType") {
        return a.issueType.localeCompare(b.issueType) * factor;
      }

      if (sortField === "status") {
        return a.status.localeCompare(b.status) * factor;
      }

      return (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()) * factor;
    });

  // Pagination calculation
  const totalPages = Math.ceil(totalTickets / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTicketsToDisplay = filteredTickets;

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString("en-GB");

  return (
    <div className="query-inbox">
      <h3 className="title">List Of User Queries</h3>

      <div className="query-toolbar">
        <div className="filters">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as "all" | TicketStatus)}>
            <option value="all">All Status</option>
            <option value="Received">Received</option>
            {/* <option value="Open">Open</option> */}
            <option value="In-progress">In-progress</option>
            <option value="Closed">Closed</option>
          </select>

          <select value={issueTypeFilter} onChange={(e) => setIssueTypeFilter(e.target.value)}>
            <option value="all">All Issue Types</option>
            {issueTypeOptions.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="sort-controls">
          <label>Sort by</label>
          <select value={sortField} onChange={(e) => setSortField(e.target.value as "createdAt" | "issueType" | "status" | "updatedAt")}>
            <option value="createdAt">Ticket Date</option>
            <option value="issueType">Issue Type</option>
            <option value="status">Status</option>
            <option value="updatedAt">Last Changed</option>
          </select>

          <button
            className="filter-btn"
            onClick={() => setSortOrder(prev => (prev === "asc" ? "desc" : "asc"))}
          >
            {sortOrder === "asc" ? "Ascending" : "Descending"}
          </button>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>No.</th>
            <th>Issue Type</th>
            <th>Raised By</th>
            <th>Child name</th>
            <th>Center</th>
            <th>Status</th>
            <th>Ticket Date</th>
            <th>Ticket Age</th>
            <th>Last Changed</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentTicketsToDisplay.length > 0 ? (
            currentTicketsToDisplay.map((ticket, i) => (
              <tr key={ticket._id} onClick={() => setSelectedTicket(ticket)}>
                <td>{startIndex + i + 1}</td>
                <td>{ticket.issueType}</td>
                <td>{ticket.parentName || ticket.childName}</td>
                <td>{ticket.childName}</td>
                <td>{(ticket as any).center || (ticket as any).centerName || (ticket as any).venueName || "—"}</td>
                <td><span className={`status-pill ${ticket.status.toLowerCase().replace(" ", "-")}`}>{ticket.status}</span></td>
                <td>{formatDate(ticket.createdAt)}</td>
                <td>{ticket.ageing}</td>
                <td>{formatDate(ticket.updatedAt)}</td>
                <td className="action">View</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={10} style={{ textAlign: "center", padding: "20px" }}>
                No queries found for the selected filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Dynamic Pagination Controls */}
      {totalPages > 0 && (
        <div className="pagination-footer">
          <p>
            Showing {totalTickets === 0 ? 0 : startIndex + 1} to {Math.min(endIndex, startIndex + currentTicketsToDisplay.length)} of {totalTickets} results
          </p>
          <div className="pagination">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
              <button
                key={pageNumber}
                onClick={() => setCurrentPage(pageNumber)}
                className={currentPage === pageNumber ? "active" : ""}
              >
                {pageNumber}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {selectedTicket && selectedTicket.status !== "Closed" && (
        <QueryModal ticket={selectedTicket} onClose={() => setSelectedTicket(null)} onUpdate={fetchTickets} />
      )}
      {selectedTicket && selectedTicket.status === "Closed" && (
        <ClosedQueryModal ticket={selectedTicket} onClose={() => setSelectedTicket(null)} />
      )}
    </div>
  );
};

export default HelpdeskQueryList;
